import { MapPin, Phone, Mail, Clock, Printer } from 'lucide-react';
import { useState } from 'react';

export default function Contact() {
  const [copyStatus, setCopyStatus] = useState('');
  const email = 'frontdesk@metabolicandprimarycare.com';

  const handleEmailClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    try {
      // Primary method: Try to open email client
      const mailtoLink = `mailto:${email}`;
      window.location.href = mailtoLink;

      // If mailto doesn't work after a short delay, try window.open
      setTimeout(() => {
        if (!document.hasFocus()) return; // Email client opened successfully
        
        window.open(mailtoLink, '_self');
        
        // If still on page after another delay, copy to clipboard as fallback
        setTimeout(() => {
          if (!document.hasFocus()) return;
          
          navigator.clipboard.writeText(email)
            .then(() => {
              setCopyStatus('Email copied to clipboard!');
              setTimeout(() => setCopyStatus(''), 2000);
            });
        }, 300);
      }, 300);
    } catch (error) {
      // Final fallback: create and click a temporary link
      const tempLink = document.createElement('a');
      tempLink.href = `mailto:${email}`;
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    }
  };

  return (
    <>
      <div 
        id="contact" 
        className="py-24 bg-primary-50"
        role="region"
        aria-labelledby="contact-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 
              id="contact-heading" 
              className="text-4xl font-bold text-gray-900 sm:text-5xl"
            >
              Contact Us
            </h2>
            <p className="mt-4 text-2xl text-gray-600">
              We're here to help. Reach out to us with any questions.
            </p>
          </div>

          {/* Large Logo Section */}
          <div className="mt-12 flex justify-center">
            <img
              src="/FMPCA Logo3-navbar.png"
              alt="FMPCA Logo"
              className="w-72 h-72 object-contain transition-all duration-300 hover:scale-105"
            />
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-1">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 
                id="contact-info" 
                className="text-2xl font-medium text-gray-900"
              >
                Get in Touch
              </h3>
              <div className="mt-8 space-y-6">
                <a 
                  href="https://www.google.com/maps/place/184+Thomas+Johnson+Dr+%23201,+Frederick,+MD+21702"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center hover:text-primary-600 transition-colors group"
                  aria-label="Our location: 184 Thomas Johnson Drive, Suite 201, Frederick, MD 21702. Opens in Google Maps"
                >
                  <MapPin className="h-7 w-7 text-secondary-600 group-hover:text-primary-600" aria-hidden="true" />
                  <span className="ml-3 text-lg text-gray-700 group-hover:text-primary-600">
                    184 Thomas Johnson Drive, Suite 201, Frederick, MD 21702
                  </span>
                </a>
                
                <a 
                  href="tel:240-949-7519"
                  className="flex items-center hover:text-primary-600 transition-colors group"
                  aria-label="Call us at 240-949-7519"
                >
                  <Phone className="h-7 w-7 text-secondary-600 group-hover:text-primary-600" aria-hidden="true" />
                  <span className="ml-3 text-lg text-gray-700 group-hover:text-primary-600">
                    240-949-7519
                  </span>
                </a>

                <a                    
                  href="fax:866-531-4159"                   
                  className="flex items-center hover:text-primary-600 transition-colors group"                   
                  aria-label="Send fax to 866-531-4159"                 
                >                   
                  <Printer className="h-7 w-7 text-secondary-600 group-hover:text-primary-600" aria-hidden="true" />                   
                  <span className="ml-3 text-lg text-gray-700 group-hover:text-primary-600">                   
                    866-531-4159                   
                  </span>                 
                </a>

                <div className="relative">
                  <a 
                    href={`mailto:${email}`}
                    onClick={handleEmailClick}
                    className="flex items-center hover:text-primary-600 transition-colors group cursor-pointer"
                    aria-label={`Email us at ${email}`}
                    rel="noopener noreferrer"
                  >
                    <Mail className="h-7 w-7 text-secondary-600 group-hover:text-primary-600" aria-hidden="true" />
                    <span className="ml-3 text-lg text-gray-700 group-hover:text-primary-600">
                      {email}
                    </span>
                  </a>
                  {copyStatus && (
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-3 py-1 rounded text-sm">
                      {copyStatus}
                    </div>
                  )}
                </div>

                <div 
                  className="flex items-center"
                  aria-label="Office hours"
                >
                  <Clock className="h-7 w-7 text-secondary-600" aria-hidden="true" />
                  <div className="ml-3 text-lg text-gray-700">
                    <p>Monday - Friday: 9:00 AM - 4:00 PM</p>
                    <p>Saturday: Appointments may be available upon request</p>
                    <p>Sunday: Closed</p> 
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Phone Call to Action Section */}
      <div 
        className="bg-primary-600 py-16"
        role="complementary"
        aria-label="Quick contact"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8">
            <Phone className="h-14 w-14 text-white" aria-hidden="true" />
            <div className="text-center sm:text-left">
              <p className="text-xl font-medium text-white" id="call-to-action">Need to speak with us?</p>
              <a 
                href="tel:240-949-7519" 
                className="text-4xl font-bold text-white hover:text-secondary-100 transition-colors"
                aria-labelledby="call-to-action"
              >
                240-949-7519
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}